/**
 * KeyboardHelpModal component for displaying keyboard shortcuts help
 */
export class KeyboardHelpModal {
  constructor() {
    this.modal = null
    this.isVisible = false
  }

  /**
   * Show the keyboard help modal
   */
  show() {
    if (this.isVisible) return

    this.createModal()
    document.body.appendChild(this.modal)
    this.isVisible = true

    // Focus management
    this.modal.focus()

    // Add event listeners
    this.setupEventListeners()
  }

  /**
   * Hide the keyboard help modal
   */
  hide() {
    if (!this.isVisible || !this.modal) return

    this.modal.remove()
    this.modal = null
    this.isVisible = false
  }

  /**
   * Create the modal DOM structure
   */
  createModal() {
    this.modal = document.createElement('div')
    this.modal.className = 'keyboard-help-modal-overlay'
    this.modal.setAttribute('tabindex', '-1')
    this.modal.setAttribute('role', 'dialog')
    this.modal.setAttribute('aria-modal', 'true')
    this.modal.setAttribute('aria-labelledby', 'keyboard-help-title')

    this.modal.innerHTML = `
            <div class="keyboard-help-modal">
                <div class="keyboard-help-header">
                    <h3 id="keyboard-help-title">⌨️ Keyboard Shortcuts</h3>
                    <button type="button" class="keyboard-help-close" aria-label="Close help">✕</button>
                </div>
                <div class="keyboard-help-content">
                    <div class="keyboard-help-section">
                        <h4>🧭 Navigation</h4>
                        <div class="keyboard-shortcut">
                            <kbd>↑</kbd> <kbd>↓</kbd>
                            <span>Move up/down between nodes</span>
                        </div>
                        <div class="keyboard-shortcut">
                            <kbd>←</kbd> <kbd>→</kbd>
                            <span>Collapse/expand nodes or navigate to parent/child</span>
                        </div>
                        <div class="keyboard-shortcut">
                            <kbd>Home</kbd> <kbd>End</kbd>
                            <span>Jump to first/last node</span>
                        </div>
                    </div>

                    <div class="keyboard-help-section">
                        <h4>⚡ Actions</h4>
                        <div class="keyboard-shortcut">
                            <kbd>Enter</kbd> <kbd>Space</kbd>
                            <span>Activate node (expand/collapse or select)</span>
                        </div>
                        <div class="keyboard-shortcut">
                            <kbd>+</kbd> <kbd>-</kbd>
                            <span>Expand/collapse current node</span>
                        </div>
                        <div class="keyboard-shortcut">
                            <kbd>*</kbd>
                            <span>Expand current node and all children</span>
                        </div>
                    </div>

                    <div class="keyboard-help-section">
                        <h4>🌍 Global Shortcuts</h4>
                        <div class="keyboard-shortcut">
                            <kbd>Ctrl</kbd> + <kbd>E</kbd>
                            <span>Expand all nodes</span>
                        </div>
                        <div class="keyboard-shortcut">
                            <kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>C</kbd>
                            <span>Collapse all nodes</span>
                        </div>
                        <div class="keyboard-shortcut">
                            <kbd>Ctrl</kbd> + <kbd>1</kbd>-<kbd>9</kbd>
                            <span>Expand to specific level (1-9)</span>
                        </div>
                        <div class="keyboard-shortcut">
                            <kbd>?</kbd>
                            <span>Show/hide this help (when tree is focused)</span>
                        </div>
                    </div>

                    <div class="keyboard-help-section">
                        <h4>🚀 Getting Started</h4>
                        <div class="keyboard-help-tip">
                            <strong>1.</strong> Make sure <strong>⌨ Keyboard Nav</strong> is enabled (should show green dot)
                        </div>
                        <div class="keyboard-help-tip">
                            <strong>2.</strong> Click on the JSON tree or press <kbd>Tab</kbd> to focus it
                        </div>
                        <div class="keyboard-help-tip">
                            <strong>3.</strong> Use arrow keys to navigate and <kbd>Enter</kbd> to interact
                        </div>
                    </div>
                </div>
            </div>
        `
  }

  /**
   * Setup event listeners for the modal
   */
  setupEventListeners() {
    if (!this.modal) return

    const closeBtn = this.modal.querySelector('.keyboard-help-close')

    // Close button
    closeBtn.addEventListener('click', () => this.hide())

    // Click outside to close
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.hide()
      }
    })

    // Escape key to close
    this.modal.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.hide()
      }
    })
  }

  /**
   * Toggle the modal visibility
   */
  toggle() {
    if (this.isVisible) {
      this.hide()
    } else {
      this.show()
    }
  }
}
